import { getPageIdFromWidgetId } from '@wix/members-area-app-definitions';
import { getAppDefinitions } from '@wix/members-area-app-definitions/get-app-definitions';
import type { IntegrationApplication } from '@wix/members-area-integration-kit';
import { MA_APP_IDS } from '@wix/members-area-integration-kit';
import type { EditorSDK, PageData } from '@wix/platform-editor-sdk';
import { createInstance } from 'i18next';

import { getTranslationFunction } from '../../../../i18n';
import type { ReferralInfo } from '../../../../types/bi';
import type { Page, PagesModificationPayload } from '../../../../types/general-settings';
import { PageType } from '../../../../types/general-settings';
import { log } from '../../../../utils/monitoring';
import { arePagesEqual } from '../../../../utils/pages';
import { APP_TOKEN, MEMBERS_LIST_PAGE_ID } from '../../../constants';
import { getAllPages } from '../../../wrappers/pages';
import { openManagePagesPanel } from '../../../wrappers/panels';
import type { WidgetPluginPointer } from '../../types';
import { deleteApps, installApps } from '../integration';
import { setProfileType } from './blog-writer';
import { getPublicApplications } from './members-area-page';

const getPagesFromMembersAreaPage = async (
  editorSDK: EditorSDK,
  widgetPluginPointers: WidgetPluginPointer[],
): Promise<Page[]> => {
  const applications = widgetPluginPointers.map(({ appDefinitionId, widgetId }) => ({
    appDefinitionId,
    pageId: getPageIdFromWidgetId(widgetId),
  }));
  const definitions = await getAppDefinitions({
    editorSDK,
    applications,
    // @ts-expect-error - versions mismatch
    i18next: createInstance(),
  });

  return definitions.map((definition) => ({
    pageType: PageType.WidgetPlugin,
    integrationApplication: definition,
    title: definition.page?.name ?? definition.loginMenuTitle ?? '',
    isInstalled: true,
  }));
};

const getMemberListPage = async (editorSDK: EditorSDK, pages: PageData[]): Promise<Page | null> => {
  const memberListPage = pages.find(({ tpaPageId }) => tpaPageId === MEMBERS_LIST_PAGE_ID);

  if (!memberListPage) {
    return null;
  }

  const [membersListDefinition] = await getAppDefinitions({
    editorSDK,
    applications: [MA_APP_IDS.ALL_MEMBERS],
    // @ts-expect-error - versions mismatch
    i18next: createInstance(),
  });

  return {
    pageType: PageType.StandAlone,
    integrationApplication: { ...membersListDefinition, pageId: MEMBERS_LIST_PAGE_ID },
    title: memberListPage.title,
    isInstalled: true,
  };
};

const getInstalledPublicPages = async (editorSDK: EditorSDK): Promise<Page[]> => {
  const [publicApplicationsInMembersAreaPage, allInstalledPages] = await Promise.all([
    getPublicApplications(editorSDK),
    getAllPages({ editorSDK }),
  ]);
  const publicPagesInMembersAreaPage = await getPagesFromMembersAreaPage(
    editorSDK,
    publicApplicationsInMembersAreaPage,
  );
  const membersListPage = await getMemberListPage(editorSDK, allInstalledPages);

  return membersListPage ? [...publicPagesInMembersAreaPage, membersListPage] : publicPagesInMembersAreaPage;
};

const getPromotionalPublicPages = async (editorSDK: EditorSDK, publicPages: Page[]): Promise<Page[]> => {
  const t = await getTranslationFunction(editorSDK, true);
  const definitions = await getAppDefinitions({
    editorSDK,
    applications: [MA_APP_IDS.ABOUT, MA_APP_IDS.FOLLOWERS, MA_APP_IDS.ALL_MEMBERS],
    // @ts-expect-error - versions mismatch
    i18next: createInstance(),
  });

  return definitions.map((definition: IntegrationApplication) => {
    const { page } = definition;
    const isMembersListPage = arePagesEqual(definition, MA_APP_IDS.ALL_MEMBERS);
    const promotionalPage = publicPages.find(({ integrationApplication }) => {
      return arePagesEqual(integrationApplication, {
        ...definition,
        pageId: isMembersListPage ? MEMBERS_LIST_PAGE_ID : definition.pageId,
      });
    });

    if (promotionalPage) {
      return promotionalPage;
    }

    return {
      pageType: isMembersListPage ? PageType.StandAlone : PageType.WidgetPlugin,
      title: isMembersListPage ? t('GeneralSettings_MembersPageTitle') : page?.name ?? '',
      integrationApplication: {
        ...definition,
        pageId: isMembersListPage ? MEMBERS_LIST_PAGE_ID : definition.pageId,
      },
      isInstalled: false,
    };
  });
};

const closeProgressBarWithoutThrowing = async (editorSDK: EditorSDK, { isError }: { isError: boolean }) => {
  try {
    await editorSDK.editor.closeProgressBar(APP_TOKEN, {
      isError,
    });
  } catch (e) {}
};

export const getGeneralSettingsPanelData = async (editorSDK: EditorSDK) => {
  const installedPublicPages = await getInstalledPublicPages(editorSDK);
  const promotionalPublicPages = await getPromotionalPublicPages(editorSDK, installedPublicPages);
  return { installedPublicPages, promotionalPublicPages };
};

export const modifyPages = async (
  editorSDK: EditorSDK,
  { appsToInstall, appsToRemove, profilePageType }: PagesModificationPayload,
) => {
  const t = await getTranslationFunction(editorSDK, true);
  const totalSteps = appsToInstall.length + appsToRemove.length + (profilePageType ? 1 : 0);
  let currentStep = 0;

  if (!totalSteps) {
    return;
  }

  try {
    await editorSDK.editor.openProgressBar(APP_TOKEN, {
      title: t('GeneralSettings_ProgressModal_Title'),
      totalSteps,
      stepTitle: t('GeneralSettings_ProgressModal_Subtitle'),
    });
  } catch (e) {
    log('V2 General settings progress bar failed to open', { extra: { e } });
  }

  try {
    if (appsToInstall.length) {
      const definitions = appsToInstall.map(({ integrationApplication }) => integrationApplication);
      await installApps(editorSDK, definitions);
      await editorSDK.editor.updateProgressBar(APP_TOKEN, {
        currentStep: (currentStep += appsToInstall.length),
        stepTitle: t('GeneralSettings_ProgressModal_Subtitle'),
      });
    }

    if (appsToRemove.length) {
      const definitions = appsToRemove.map(({ integrationApplication }) => integrationApplication);
      await deleteApps(editorSDK, definitions);
      await editorSDK.editor.updateProgressBar(APP_TOKEN, {
        currentStep: (currentStep += appsToRemove.length),
        stepTitle: t('GeneralSettings_ProgressModal_Subtitle'),
      });
    }

    if (profilePageType) {
      await setProfileType(editorSDK, profilePageType);
      await editorSDK.editor.updateProgressBar(APP_TOKEN, {
        currentStep: ++currentStep,
        stepTitle: t('GeneralSettings_ProgressModal_Subtitle'),
      });
    }

    await closeProgressBarWithoutThrowing(editorSDK, { isError: false });
  } catch (e) {
    await closeProgressBarWithoutThrowing(editorSDK, { isError: true });

    throw e;
  }
};

export const modifyPagesAndOpenManagePages = async (
  editorSDK: EditorSDK,
  options: PagesModificationPayload,
  referralInfo?: ReferralInfo,
) => {
  await modifyPages(editorSDK, options);
  return openManagePagesPanel({ editorSDK, eventPayload: { pageRef: undefined, referralInfo } });
};
